import React, { useRef } from 'react';
import styled from 'astroturf/react';
import { Popover, PopoverMode, PopoverProps } from 'js/design_system/Popover';

// Must match the `10px` border widths in the Nubbin styles below — Tooltip
// reserves `NUBBIN_SIZE` extra space on the popover's side facing the
// activator so the pip can sit in the gap without overlapping the body.
const NUBBIN_SIZE = 10;

const TooltipContainer = styled.div`
  display: flex;
  padding: 8px var(--button-sm-standard-top-bottom-padding, 12px);

  align-items: center;
  border-radius: var(--border-radius-radius-sm);
  background: var(--tooltip-color, var(--background-black, #141413));

  color: var(--tooltip-text-color, var(--text-white, #fff));
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const Nubbin = styled.div`
  position: absolute;
  width: 0;
  height: 0;

  &[data-placement^='top'] {
    bottom: var(--tooltip-offset, 4px);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid
      var(--tooltip-color, var(--background-black, #141413));
  }
  &[data-placement^='bottom'] {
    top: var(--tooltip-offset, 4px);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid
      var(--tooltip-color, var(--background-black, #141413));
  }
  &[data-placement^='left'] {
    right: var(--tooltip-offset, 4px);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 10px solid
      var(--tooltip-color, var(--background-black, #141413));
  }
  &[data-placement^='right'] {
    left: var(--tooltip-offset, 4px);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid
      var(--tooltip-color, var(--background-black, #141413));
  }
`;

/**
 * A basic tooltip component with Academia styling. This component uses
 * {@link Popover} as its parent, adding some additional styling.
 *
 * The nubbin is positioned along the popover's cross axis by floating-ui's
 * `arrow` middleware so it tracks the activator's center even when `shift`
 * or `flip` reposition the bubble — including when `placement` is given as
 * `'top-end'`/`'top-start'`/etc.
 *
 * Props:
 * - `placement`: Controls the placement of the tooltip relative to the activator.
 *                Choose between 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start' | 'left-start' | 'right-start' | 'top-end' | 'bottom-end' | 'left-end' | 'right-end'
 * - `offset`: Controls the offset of the tooltip relative to the activator.
 * - `closeOnClick`: Controls whether the tooltip should close when the activator is clicked.
 * - `popoverRef`: A ref for the tooltip body.
 * - `render`: A function that returns the tooltip content.
 * - `children`: The activator element.
 * - `mode`: Controls the Popover mode of the tooltip.
 *           Choose between 'click' | 'hover' | 'any'
 * - `tooltipColor`: Optional color for the tooltip and nubbin.
 * - `tooltipTextColor`: Optional color for the tooltip text.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Tooltip } from 'js/design_system/Tooltip';
 *
 * <Tooltip
 *   render={() => <p>I'm the tooltip content!</p>}
 * >
 *   <p>Click me to activate!</p>
 * </Tooltip>
 * ```
 */
export const Tooltip: React.FC<
  PopoverProps & { tooltipColor?: string; tooltipTextColor?: string }
> = ({
  mode = PopoverMode.Hover,
  placement = 'top',
  offset = 4,
  closeOnClick = false,
  popoverRef,
  render,
  children,
  tooltipColor,
  tooltipTextColor,
  ...props
}) => {
  const nubbinRef = useRef<HTMLDivElement | null>(null);

  const tooltipStyle = {} as React.CSSProperties & {
    '--tooltip-color'?: string;
    '--tooltip-text-color'?: string;
    '--tooltip-offset'?: string;
  };
  if (tooltipColor) {
    tooltipStyle['--tooltip-color'] = tooltipColor;
  }
  if (tooltipTextColor) {
    tooltipStyle['--tooltip-text-color'] = tooltipTextColor;
  }
  tooltipStyle['--tooltip-offset'] = `${offset}px`;

  return (
    <Popover
      mode={mode}
      placement={placement}
      offset={offset + NUBBIN_SIZE}
      closeOnClick={closeOnClick}
      arrowRef={nubbinRef}
      arrowPadding={8}
      containerStyle={tooltipStyle}
      render={() => (
        <>
          <TooltipContainer ref={popoverRef}>{render()}</TooltipContainer>
          <Nubbin ref={nubbinRef} data-placement={placement} />
        </>
      )}
      {...props}
    >
      {children}
    </Popover>
  );
};
